import { Fragment, type ReactNode } from "react";
import classNames from "classnames";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Tag } from "./Tag";
import styles from "./TagOverflow.module.scss";

export interface TagOverflowItem {
    id: string;
    /** The tag as drawn, inline and in the popover. */
    content: ReactNode;
    /** Its name in the +N trigger's accessible name. */
    label: string;
}

interface TagOverflowProps {
    items: readonly TagOverflowItem[];
    /** Items drawn before the +N tag. @default 3 */
    max?: number;
    /** For the +N trigger; a board card passes -1 so the card stays the Tab stop. */
    triggerTabIndex?: number;
    /** The +N trigger's tooltip, e.g. "Other fields". */
    triggerTooltip?: string;
    className?: string;
}

/**
 * A wrapping row of tags capped at `max`, then a "+N" tag that opens a popover
 * with the rest. The trigger is a real button and the popover is portaled, so
 * inside a board card neither opens nor drags the card.
 */
export const TagOverflow = ({
    items,
    max = 3,
    triggerTabIndex,
    triggerTooltip,
    className,
}: Readonly<TagOverflowProps>) => {
    const hiddenItems = items.slice(max);

    return (
        <span className={classNames(styles.root, className)}>
            {items.slice(0, max).map((item) => (
                <Fragment key={item.id}>{item.content}</Fragment>
            ))}
            {hiddenItems.length > 0 && (
                <Popover>
                    <Tooltip content={triggerTooltip}>
                        <PopoverTrigger>
                            <button
                                type="button"
                                className={styles.trigger}
                                tabIndex={triggerTabIndex}
                                aria-label={`View ${hiddenItems.length} more: ${hiddenItems.map((item) => item.label).join(", ")}`}
                            >
                                <Tag
                                    size="sm"
                                    color="neutral"
                                    outline
                                    numeric
                                    className={styles.moreTag}
                                >
                                    +{hiddenItems.length}
                                </Tag>
                            </button>
                        </PopoverTrigger>
                    </Tooltip>
                    <PopoverContent
                        align="start"
                        side="bottom"
                        className={styles.popup}
                    >
                        {hiddenItems.map((item) => (
                            <Fragment key={item.id}>{item.content}</Fragment>
                        ))}
                    </PopoverContent>
                </Popover>
            )}
        </span>
    );
};
